import React, {
    useState, useEffect, useRef,
    useImperativeHandle, forwardRef,
} from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import SignatureInput from './SignatureInput';
import { FormData, ValidationErrors, Form11Props, FormValidateHandle } from './typesIN';
import { useFieldLock } from './useFieldLock';

const removeSpecialChars = (v: string) => v.replace(/[!#$%^&*()_=+;:'"<>/|\\{}[\]~`]/g, '');
const allowOnlyAlphabets = (v: string) => v.replace(/[^a-zA-Z\s.]/g, ''); // allow dot for initials
const toDateOrUndefined = (v: any): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? undefined : d;
};
const TODAY = new Date();

const Form11 = forwardRef<FormValidateHandle, Form11Props>(
    ({ onSubmit, onDraftSave, onBack, initialData = {}, fieldEditability = {}, autoSignature = '', isFemale = false }, ref) => {
        const toast = useRef<Toast>(null);

        const [formData, setFormData] = useState<FormData>({ internationalWorker: 'no', ...initialData });
        const [errors, setErrors] = useState<ValidationErrors>({});
        const [acknowledgement, setAcknowledgement] = useState(false);

        useEffect(() => {
            if (initialData && Object.keys(initialData).length > 0) {
                setFormData(prev => {
                    const merged = { ...prev };
                    Object.entries(initialData).forEach(([key, val]) => {
                        const existing = prev[key];
                        const isEmpty = existing === undefined || existing === null || existing === '';
                        if (val !== undefined && val !== null && val !== '' && isEmpty) merged[key] = val;
                    });
                    return merged;
                });
            }
        }, [initialData]);

        // Auto-populate member signature from Form2 whenever it changes
        useEffect(() => {
            if (autoSignature) {
                setFormData(prev => ({ ...prev, memberSignature: autoSignature }));
            }
        }, [autoSignature]);

        const ALWAYS_READONLY = ['memberName', 'fatherName', 'memberNameConfirm', 'place', 'date', 'joinedOn', 'signatureDate', 'employerSignature', 'internationalWorker'];
        const ALWAYS_EDITABLE = ['email', 'mobile', 'uan', 'country', 'passport', 'passportValidity', 'bankAccount', 'ifsc', 'aadhar', 'pan', 'memberSignature', 'epfMember', 'epsMember', 'exitDate', 'dob', 'gender', 'maritalStatus'];
        const isLocked = useFieldLock(formData, fieldEditability, ALWAYS_READONLY, ALWAYS_EDITABLE);

        const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        useEffect(() => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
            draftTimerRef.current = setTimeout(() => { onDraftSave?.(formData); }, 500);
            return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData]);

        const genderOptions = [{ label: 'Select', value: '' }, { label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }, { label: 'Other', value: 'other' }];
        const maritalStatusOptions = [{ label: 'Select', value: '' }, { label: 'Unmarried', value: 'unmarried' }, { label: 'Married', value: 'married' }, { label: 'Divorced', value: 'divorced' }, { label: 'Widow', value: 'widow' }, { label: 'Widower', value: 'widower' }];
        const yesNoOptions = [{ label: 'Select', value: '' }, { label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }];
        const countryOptions = [{ label: 'Select', value: '' }, { label: 'United States', value: 'us' }, { label: 'United Kingdom', value: 'uk' }, { label: 'Canada', value: 'ca' }, { label: 'Australia', value: 'au' }, { label: 'Other', value: 'other' }];

        const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
        const validateMobile = (v: string) => /^\d{10}$/.test(v);
        const validatePAN = (v: string) => /^[A-Z]{5}\d{4}[A-Z]$/.test(v);
        const validateAadhar = (v: string) => /^\d{12}$/.test(v);
        const validateIFSC = (v: string) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v);
        const validatePassport = (v: string) => /^[A-Z][0-9]{7}$/.test(v);
        const validateUAN = (v: string) => /^\d{12}$/.test(v);
        const validateBankAcc = (v: string) => /^\d{9,18}$/.test(v);

        const isInternationalWorker = formData.internationalWorker === 'yes';

        const handleInputChange = (field: string, value: any) => {
            const nameFields = ['memberName', 'fatherName', 'memberNameConfirm', 'memberSignature', 'place'];
            if (typeof value === 'string') value = nameFields.includes(field) ? allowOnlyAlphabets(value) : removeSpecialChars(value);
            // When international worker switches to 'no', clear passport/country data
            if (field === 'internationalWorker' && value === 'no') {
                setFormData(prev => ({
                    ...prev,
                    internationalWorker: 'no',
                    country: '',
                    passport: '',
                    passportValidity: '',
                }));
                if (errors.country || errors.passport || errors.passportValidity) {
                    setErrors(prev => ({ ...prev, country: '', passport: '', passportValidity: '' }));
                }
                return;
            }
            setFormData(prev => ({ ...prev, [field]: value }));
            if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
        };

        const validateForm = (): boolean => {
            const e: ValidationErrors = {};

            if (!formData.memberName) e.memberName = 'Member Name is required';
            if (!formData.fatherName) e.fatherName = "Father/Husband's Name is required";
            if (!formData.dob) e.dob = 'Date of Birth is required';
            if (!formData.gender) e.gender = 'Gender is required';
            if (!formData.maritalStatus) e.maritalStatus = 'Marital Status is required';

            if (!formData.email)
                e.email = 'Email is required';
            else if (!validateEmail(formData.email as string))
                e.email = 'Invalid email — expected format: name@domain.com';

            if (!formData.mobile)
                e.mobile = 'Mobile Number is required';
            else if (!validateMobile(formData.mobile as string))
                e.mobile = 'Mobile must be 10 digits — e.g. 9876543210';

            if (!formData.uan)
                e.uan = 'Universal Account Number is required';
            else if (!validateUAN(String(formData.uan)))
                e.uan = 'UAN must be 12 digits — e.g. 123456789012';

            // internationalWorker is locked to 'no' — no validation needed

            if (isInternationalWorker) {
                if (!formData.country) e.country = 'Country is required';
                if (!formData.passport)
                    e.passport = 'Passport Number is required';
                else if (!validatePassport(formData.passport as string))
                    e.passport = 'Invalid passport — expected format: A1234567 (1 letter + 7 digits)';
                if (!formData.passportValidity) e.passportValidity = 'Passport Validity is required';
            }

            const bankVal = String(formData.bankAccount ?? '').trim();
            if (bankVal && !validateBankAcc(bankVal))
                e.bankAccount = 'Bank account must be 9–18 digits — no spaces or letters';

            const ifscVal = String(formData.ifsc ?? '').trim();
            if (ifscVal && !validateIFSC(ifscVal))
                e.ifsc = 'Invalid IFSC — expected format: ABCD0123456 (4 letters, 0, 6 alphanumeric)';

            if (!formData.aadhar)
                e.aadhar = 'Aadhar Number is required';
            else if (!validateAadhar(formData.aadhar as string))
                e.aadhar = 'Aadhar must be 12 digits — e.g. 123456789012';

            if (!formData.pan)
                e.pan = 'PAN Number is required';
            else if (!validatePAN(formData.pan as string))
                e.pan = 'Invalid PAN — expected format: ABCDE1234F (5 letters, 4 digits, 1 letter)';

            if (!formData.memberSignature) e.memberSignature = 'Member Signature is required';
            if (!acknowledgement) e.acknowledgement = 'Please confirm the acknowledgement';

            setErrors(e);

            const isValid = Object.keys(e).length === 0;
            if (!isValid) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Incomplete Form',
                    detail: 'Please fill in all mandatory fields before submitting.',
                    life: 4000,
                });
            }
            return isValid;
        };

        // ── Expose validate() to parent via ref ──────────────────────────────
        useImperativeHandle(ref, () => ({ validate: validateForm }));

        const handleSubmit = () => {
            if (validateForm()) onSubmit?.(formData);
            else window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        return (
            <div className="form-container">
                <Toast ref={toast} position="top-right" />
                <h2 className="form-title">Form 11</h2>

                {/* Row 1 */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Member Name*</label>
                        <InputText disabled value={formData.memberName as string || ''} placeholder="Auto populated" maxLength={100} />
                    </div>
                    <div className="form-field">
                        <label>Father/Husband's Name*</label>
                        <InputText disabled value={formData.fatherName as string || ''} placeholder="Auto populated" maxLength={100} />
                    </div>
                    <div className="form-field">
                        <label>Date of Birth* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.dob)} onChange={(e) => handleInputChange('dob', e.value)} placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput maxDate={TODAY} className={errors.dob ? 'p-invalid' : ''} />
                        {errors.dob && <small className="p-error">{errors.dob}</small>}
                    </div>
                    <div className="form-field">
                        <label>Gender*</label>
                        <Dropdown value={formData.gender} options={genderOptions} onChange={(e) => handleInputChange('gender', e.value)} placeholder="Select" className={errors.gender ? 'p-invalid' : ''} />
                        {errors.gender && <small className="p-error">{errors.gender}</small>}
                    </div>
                    <div className="form-field">
                        <label>Marital Status*</label>
                        <Dropdown value={formData.maritalStatus} options={maritalStatusOptions} onChange={(e) => handleInputChange('maritalStatus', e.value)} placeholder="Select" className={errors.maritalStatus ? 'p-invalid' : ''} />
                        {errors.maritalStatus && <small className="p-error">{errors.maritalStatus}</small>}
                    </div>
                </div>

                {/* Row 2 */}
                <div className="form-row white-bg">
                    <div className="form-field">
                        <label>Email ID*</label>
                        <InputText type="email" placeholder="name@domain.com" value={formData.email as string || ''} onChange={(e) => handleInputChange('email', e.target.value)} maxLength={100} className={errors.email ? 'p-invalid' : ''} />
                        {errors.email && <small className="p-error">{errors.email}</small>}
                    </div>
                    <div className="form-field">
                        <label>Mobile Number*</label>
                        <InputText
                            placeholder="10-digit — e.g. 9876543210"
                            value={formData.mobile as string || ''}
                            onChange={(e) => { if (!e.target.value || /^\d{0,10}$/.test(e.target.value)) handleInputChange('mobile', e.target.value); }}
                            maxLength={10}
                            className={errors.mobile ? 'p-invalid' : ''}
                        />
                        {errors.mobile && <small className="p-error">{errors.mobile}</small>}
                    </div>
                    <div className="form-field">
                        <label>EPF Scheme Member</label>
                        <Dropdown value={formData.epfMember} options={yesNoOptions} onChange={(e) => handleInputChange('epfMember', e.value)} placeholder="Select" />
                    </div>
                    <div className="form-field">
                        <label>EPS Scheme Member</label>
                        <Dropdown value={formData.epsMember} options={yesNoOptions} onChange={(e) => handleInputChange('epsMember', e.value)} placeholder="Select" />
                    </div>
                    <div className="form-field">
                        <label>Universal Account Number*</label>
                        <InputText
                            placeholder="12-digit — e.g. 123456789012"
                            value={formData.uan as string || ''}
                            onChange={(e) => handleInputChange('uan', e.target.value.replace(/\D/g, '').slice(0, 12))}
                            maxLength={12}
                            className={errors.uan ? 'p-invalid' : ''}
                        />
                        {errors.uan && <small className="p-error">{errors.uan}</small>}
                    </div>
                </div>

                {/* Row 3 */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Exit Date <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.exitDate)} onChange={(e) => handleInputChange('exitDate', e.value)} placeholder="Select date" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                    <div className="form-field">
                        <label>International Worker</label>
                        <InputText value="No" disabled />
                    </div>
                    {isInternationalWorker && (
                        <div className="form-field">
                            <label>Country of Origin*</label>
                            <Dropdown value={formData.country} options={countryOptions} onChange={(e) => handleInputChange('country', e.value)} placeholder="Select" className={errors.country ? 'p-invalid' : ''} />
                            {errors.country && <small className="p-error">{errors.country}</small>}
                        </div>
                    )}
                </div>

                {/* Row 4 */}
                <div className="form-row white-bg">
                    {isInternationalWorker && (
                        <>
                            <div className="form-field">
                                <label>Passport Number*</label>
                                <InputText
                                    placeholder="e.g. A1234567"
                                    value={formData.passport as string || ''}
                                    onChange={(e) => handleInputChange('passport', e.target.value.toUpperCase())}
                                    maxLength={8}
                                    className={errors.passport ? 'p-invalid' : ''}
                                />
                                {errors.passport && <small className="p-error">{errors.passport}</small>}
                            </div>
                            <div className="form-field">
                                <label>Passport Validity*</label>
                                <InputText
                                    placeholder="e.g. 31/12/2030"
                                    value={formData.passportValidity as string || ''}
                                    onChange={(e) => handleInputChange('passportValidity', e.target.value)}
                                    maxLength={10}
                                    className={errors.passportValidity ? 'p-invalid' : ''}
                                />
                                {errors.passportValidity && <small className="p-error">{errors.passportValidity}</small>}
                            </div>
                        </>
                    )}
                    <div className="form-field">
                        <label>Bank Account</label>
                        <InputText
                            placeholder="9–18 digit account number"
                            value={formData.bankAccount as string || ''}
                            onChange={(e) => { if (!e.target.value || /^\d+$/.test(e.target.value)) handleInputChange('bankAccount', e.target.value); }}
                            className={errors.bankAccount ? 'p-invalid' : ''}
                        />
                        {errors.bankAccount && <small className="p-error">{errors.bankAccount}</small>}
                    </div>
                    <div className="form-field">
                        <label>IFSC Code</label>
                        <InputText
                            placeholder="e.g. SBIN0001234"
                            value={formData.ifsc as string || ''}
                            onChange={(e) => handleInputChange('ifsc', e.target.value.toUpperCase())}
                            maxLength={11}
                            className={errors.ifsc ? 'p-invalid' : ''}
                        />
                        {errors.ifsc && <small className="p-error">{errors.ifsc}</small>}
                    </div>
                    <div className="form-field">
                        <label>Aadhar Number*</label>
                        <InputText
                            placeholder="12-digit — e.g. 123456789012"
                            value={formData.aadhar as string || ''}
                            onChange={(e) => { if (!e.target.value || /^\d{0,12}$/.test(e.target.value)) handleInputChange('aadhar', e.target.value); }}
                            maxLength={12}
                            className={errors.aadhar ? 'p-invalid' : ''}
                        />
                        {errors.aadhar && <small className="p-error">{errors.aadhar}</small>}
                    </div>
                </div>

                {/* Row 5 */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>PAN Number*</label>
                        <InputText
                            placeholder="e.g. ABCDE1234F"
                            value={formData.pan as string || ''}
                            onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                            maxLength={10}
                            className={errors.pan ? 'p-invalid' : ''}
                        />
                        {errors.pan && <small className="p-error">{errors.pan}</small>}
                    </div>
                    <div className="form-field">
                        <label>Place*</label>
                        <InputText disabled value={formData.place as string || ''} placeholder="Auto populated" />
                    </div>
                    <div className="form-field">
                        <label>Date* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.date)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                </div>

                {/* Signature */}
                <div className="form-row white-bg two-column">
                    <div className="form-field full-width">
                        <label>
                            Signature of Member*
                            {autoSignature && formData.memberSignature === autoSignature && (
                                <span className="address-copy-badge">auto-filled from Form 2</span>
                            )}
                        </label>
                        <SignatureInput value={formData.memberSignature as string || ''} onChange={(v) => handleInputChange('memberSignature', v)} placeholder="Type your full name" className={errors.memberSignature ? 'p-invalid' : ''} />
                        {errors.memberSignature && <small className="p-error">{errors.memberSignature}</small>}
                    </div>
                    <div className="form-field full-width">
                        <label>Member's Name*</label>
                        <InputText disabled={isLocked('memberNameConfirm')} value={formData.memberNameConfirm as string || ''} placeholder="Auto populated" maxLength={100} />
                    </div>
                </div>

                {/* Acknowledgement */}
                <div className="acknowledgement-section">
                    <div className={`acknowledgement-checkbox-row ${errors.acknowledgement ? 'has-error' : ''}`}>
                        <input type="checkbox" className="ack-checkbox" checked={acknowledgement}
                            onChange={(e) => { setAcknowledgement(e.target.checked); if (errors.acknowledgement) setErrors(prev => ({ ...prev, acknowledgement: '' })); }}
                            disabled={!formData.memberSignature} />
                        <span className="acknowledgement-label" onClick={() => { if (formData.memberSignature) setAcknowledgement(!acknowledgement); }}>
                            I hereby certify that the digital signature provided is true and the information furnished is accurate to the best of my knowledge.
                        </span>
                    </div>
                    {!formData.memberSignature && <small className="field-hint">Please enter your signature above to enable this checkbox</small>}
                    {errors.acknowledgement && <small className="p-error">{errors.acknowledgement}</small>}
                </div>

                {/* Joined On + Date */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Joined On* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.joinedOn)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                    <div className="form-field">
                        <label>Date* <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar value={toDateOrUndefined(formData.signatureDate)} disabled placeholder="Auto populated" showIcon dateFormat="dd/mm/yy" readOnlyInput />
                    </div>
                </div>

                {/* Employer Signature */}
                <div className="form-row white-bg two-column">
                    <div className="form-field full-width">
                        <label>Signature of the Employer/Authorised Officer</label>
                        <InputText disabled value={formData.employerSignature as string || ''} placeholder="To be filled by employer" />
                    </div>
                </div>

                <div className="form-actions">
                    <Button
                        label={isFemale ? "Continue" : "Review and Submit"}
                        onClick={handleSubmit}
                        className="continue-btn p-button-primary"
                    />
                </div>
            </div>
        );
    }
);

Form11.displayName = 'Form11';
export default Form11;
